/**
 * مركزُ المساعدة (27 سبتمبر 2026) — مدخلٌ لكل شاشةٍ في القوائم، مفهرسٌ بمسارها.
 *
 * الصفحةُ تُبنى **من القائمة نفسِها** (`visibleNavGroups`): لكل بندٍ يراه صاحبُ الحساب اسمُه ومدخلُه
 * هنا — فلا يظهر شرحُ شاشةٍ لا يملكها، ولا تُضاف شاشةٌ بلا شرح: `test/help-content.test.ts` يسقط عند
 * أول مسارٍ في القوائم بلا مدخل. **والنصُّ يصف ما تفعله المنصةُ فعلاً** — لا وعدٌ لا يطبّقه الكود.
 */
import type { SectionKind } from '@/components/Rafif';

export interface HelpEntry {
  /** ماذا تفعل هنا — جملةٌ واحدة */
  what: string;
  /** خطواتٌ قصيرة مرقّمة */
  steps: string[];
  tips?: string[];
}

export const HELP: Record<string, HelpEntry> = {
  // ─── عامٌّ لكل من دخل ───
  '/me/home': {
    what: 'صفحتك الأولى: ما ينتظرك الآن مرتّباً بالأهمية، وأزرار الاختصارات التي اخترتها.',
    steps: ['اقرأ بنود «ما ينتظرك» من الأعلى — الخطر أولاً ثم التحذير.', 'اضغط أي بندٍ ليأخذك إلى مكانه مباشرة.', 'خصّص اختصاراتك من زرّ «خصّص» بجانبها.'],
  },
  '/me/notifications': {
    what: 'كل تنبيهاتك داخل المنصة في مكانٍ واحد، والجديد منها مميَّز.',
    steps: ['اضغط التنبيه لتفتح ما يخصّه ويُعلَّم مقروءاً.', 'استعمل «علّم الكل مقروءاً» حين تنتهي منها.', 'اختر من «تفضيلات التنبيه» ما يصلك بالبريد وإشعار الجوال.'],
    tips: ['التنبيه داخل المنصة لا يُطفأ — ما يُطفأ هو البريد وإشعار الجوال.'],
  },
  '/me/account': {
    what: 'بيانات حسابك: الاسم والجوال والبريد والصورة وكلمة المرور والتحقّق بخطوتين وجلساتك المفتوحة.',
    steps: ['عدّل الاسم أو الجوال واحفظ — تغيير الجوال يُسقط توثيقه حتى توثّقه من جديد.', 'غيّر بريد الدخول برمزٍ يصل البريد الجديد.', 'من «جلساتي» أنهِ أي جلسةٍ على جهازٍ لا تعرفه.'],
    tips: ['فعّل التحقّق بخطوتين إن كان حسابك يرى بيانات أطفال أو مالاً.'],
  },

  '/me/drafts': {
    what: 'كلُّ ما بدأتَه ولم تُنهِه بعد — نموذجٌ أو خطةٌ أو تحضيرٌ أو تقريرٌ أو خبرٌ أو منشورٌ أو دورة — في مكانٍ واحد، وللإدارة مسوداتُ الكادر أيضاً.',
    steps: ['افتح المسودة لتكملها من حيث توقّفت.', 'احذف ما لم تعد تريده — من زرّ «احذف» أو بتحديد عدّةٍ معاً؛ يبقى أثرُ الحذف في سجل التدقيق.', 'الإدارةُ والمشرفُ يريان «مسودات الكادر» ويحذفان ما يحذفه النظام لهم (النماذجُ للمشرف، والبقيةُ للإدارة).'],
    tips: ['زرّ «المسودات» في أعلى الشاشة يظهر بعدّادٍ حين يكون لديك مسودات — وتجده هنا دائماً.'],
  },

  // ─── إدارة المؤسسة والكادر ───
  '/app': {
    what: 'لوحة المؤسسة اليومية: أرقام اليوم، وطلبات الربط، وآخر ما حدث، وملاحظات إدارة المنصة إن وُجدت.',
    steps: ['ابدأ بما ينتظرك في أعلى اللوحة.', 'افتح «طلبات الربط» لتقبل الطلبة الجدد.', 'تابع «آخر ما حدث في مؤسستك» لتعرف من فعل ماذا.'],
  },
  '/app/review': {
    what: 'مركز المراجعة: الخطط والأهداف والتقارير التي تنتظر مراجعتك أو اعتمادك، مرتّبةً بمهلتها.',
    steps: ['ابدأ بما تجاوز مهلته (يظهر أولاً).', 'افتح البند، اقرأه، ثم اعتمده أو أعده بملاحظة.', 'الإرشاد في طابورٍ منفصل عن التربية الخاصة.'],
    tips: ['لا يعتمد أحدٌ ما كتبه بنفسه — العيون الأربع تُفرض في الخادم.'],
  },
  '/app/students': {
    what: 'الطلبة الملتحقون بمؤسستك الآن، وملفّ كل طالب.',
    steps: ['ابحث بالاسم أو الرقم المرجعي.', 'افتح الطالب لترى ملفّه وخططه وجلساته ومرفقاته.', 'الطالب الجديد يدخل من «طلبات الربط» لا من هنا.'],
    tips: ['وليّ الأمر هو من يضيف طفله ويطلب ربطه — المؤسسة تقبل الطلب.'],
  },
  '/app/link-requests': {
    what: 'طلبات أولياء الأمور لربط أبنائهم بمؤسستك — القبول يُلحق الطفل ويفعّل ولاية وليّ الأمر.',
    steps: ['راجع بيانات الطفل ووليّ الأمر في الطلب.', 'اقبل الطلب — يُحجز له مقعد إن كان للمؤسسة سقفُ مقاعد.', 'أو ارفضه بسببٍ مكتوب يصل وليّ الأمر.'],
  },
  '/app/plans': {
    what: 'الخطط الفردية وأهدافها — تُكتب، تُراجَع، ثم تُعتمد وتُتابع.',
    steps: ['أنشئ خطةً لطالبٍ مُسندٍ إليك من «خطة جديدة».', 'أضف الأهداف بمعيار إتقانٍ لكلٍّ منها.', 'أرسلها للمراجعة — لا تُتابع قبل اعتمادها.'],
    tips: ['تُشارَك الخطة المعتمدة مع معلّم الطالب من صفحة الخطة.', 'بعد الاعتماد: «جدولة جلسات الخطة» في صفحتها — اختر أيام الأسبوع والوقت فتُجدول الجلسات حتى نهاية الخطة وتُتخطّى العطل.'],
  },
  '/app/preparations': {
    what: 'التحضير اليومي لجلسة: أهدافٌ من الخطة النشطة، ولكلّ هدفٍ مدّته واستراتيجيته وأدواته.',
    steps: ['اختر الطالب والتاريخ وقالب تخصّصك إن أردت.', 'أضف الأهداف من الخطة النشطة وحدّد لكلٍّ مدّتها واستراتيجيتها.', 'أكمل التحضير — الجلسة لا تبدأ بلا تحضيرٍ مكتمل.'],
  },
  '/app/sessions': {
    what: 'الجلسات: جدولتها (حضورية أو عن بُعد)، وبدؤها، وتسجيل المحاولات، وإنهاؤها، ثم تقريرها. الجلسة عن بُعد مكالمةٌ داخل المنصة على خادم رفرفة في الأردن، تُسجَّل وتُحذف بعد سبعة أيام.',
    steps: ['جدول الجلسة واربطها بتحضيرها — واختر «عن بُعد» إن كانت مكالمة.', 'ابدأها في موعدها — تحتاج موافقة تسجيلٍ سارية من وليّ الأمر؛ وفي الجلسة عن بُعد يظهر زرّ «ادخل المكالمة» ويصل وليَّ الأمر زرُّ الدخول في بطاقة طفله.', 'سجّل المحاولات أثناءها، ثم أنهِها واكتب التقرير — والإنهاء يُغلق المكالمة للطرفين.', 'لطالبٍ لم توضع خطتُه بعد: اختر «جلسة تقييم» في الجدولة — بلا خطةٍ ولا تحضير، وتكتب ملاحظات التقييم في صفحتها.', 'غاب الطالب أو تريد تعويضاً أو تكثيفاً؟ «غيّر الموعد» من صفحة الجلسة قبل بدئها — بلا موافقة، ويصل وليَّ الأمر تنبيهٌ بالموعد الجديد.'],
  },
  '/app/schedule': {
    what: 'تقويمك بتوقيت عمّان: شهرٌ بمربّعات الأيام، وتحت كل يومٍ نقاطٌ بلون ما فيه — جلسة تربية خاصة، جلسة إرشاد، حصة. الإدارة والمشرف يرون تقويم المؤسسة كلِّها أو تقويم كادرٍ بعينه.',
    steps: ['اضغط يوماً أو زرّ التركيز في أول الصفّ لتكبير الأسبوع إلى عرض الساعات.', 'اضغط موعداً لتفتح صفحته، و«الشهر» للعودة.', 'أخفِ نوعاً أو أظهره من الدليل الملوّن فوق الشبكة.', 'لمن يجدول: اضغط وقتاً فارغاً في الأسبوع فتُفتح جدولةُ جلسةٍ فيه.'],
    tips: ['الألوان ثابتة في كل المنصة: كل نوعٍ بلونه هنا وفي منتقي التاريخ.', 'العطل الرسمية وعطل المؤسسة تظهر أيامُها مظلَّلةً باسمها.'],
  },
  '/me/calendar': {
    what: 'تقويمك الشخصي: جلسات أطفالك وحصص صفوفهم (وليّ الأمر)، أو استشاراتك (المستفيد والمهني) — شهرٌ بمربّعات، وأسبوعٌ بالساعات.',
    steps: ['اضغط يوماً لتكبير أسبوعه.', 'اضغط موعداً لتفتح صفحته.', '«اليوم» يعيدك إلى الأسبوع الحالي.'],
  },
  '/app/cases': {
    what: 'لوحة الحالات: أعداد حيّة (الطلبة، الخطط بحالاتها، جلسات الأسبوع، ما ينتظر المراجعة، التنبيهات المفتوحة) وقوائم ما تأخّر — خطط بلا اعتماد، جلسات بلا تقرير، واجبات بلا مراجعة، فيديوهات لم تؤكّدها الأسر، وطلبة بلا خطة معتمدة.',
    steps: ['اقرأ الأعداد في الأعلى.', 'افتح كل قائمة «ما تأخّر» وعالجها من رابطها.', 'ما يختفي من القوائم عولج — لا زرّ «تم» هنا.'],
    tips: ['كلُّ ما هنا يُقرأ من الجداول القائمة لحظة الفتح — لا سجلّ ثانٍ.'],
  },
  '/app/promotion': {
    what: 'ترقية نهاية السنة دفعةً واحدة: لكل صفٍّ تقترح المنصة المرحلة التالية من القالب، وتعدّل أنت ما شئت وتستثني من يُعيد السنة، ثم تنفّذ — فيُرقّى الطلبة، وتُنهى عضوياتهم في الصف القديم، ويُنشأ الصف الجديد ويُضمّون إليه، ويُطفأ القديم إن خلا. لا يُحذف شيء.',
    steps: ['اكتب السنة الدراسية الجديدة.', 'راجع لكل صفّ: المرحلة الهدف واسم الصف الجديد، واستثنِ من يبقى.', 'اختر «افتح فصلَي السنة الجديدة» إن أردتهما من القالب.', 'نفّذ — وتجد السجل أسفل الصفحة.'],
    tips: ['الصف الجديد بلا معلّمين — أضفهم من «الصفوف والشُّعب».', 'آخر مرحلة في القالب بلا مرحلة تالية: اتركها فارغة تعني تخرّجاً.'],
  },
  '/app/guardian-invitations': {
    what: 'أضف طلبتك إلى رفرفة عن طريق أولياء أمورهم: ترسل لكلٍّ منهم دعوةً ببريده، وحين يقبلها ينشئ ملفَّ طفله بنفسه وينضمّ الطفل إلى مؤسستك فوراً — فلا تُدخل أنت ملفات الطلبة.',
    steps: ['لطالبٍ واحد: «وليّ أمر واحد» — اكتب اسم الطفل وميلاده وجنسه وبريد وليّ أمره ثم «أرسل الدعوة».', 'لقائمة كاملة: «قائمة من إكسل» ← «نزّل القالب» ← عبّئه في إكسل صفّاً لكل طفل ← ارفعه (أو الصق صفوفه).', 'اضغط «اعرض القائمة قبل الإرسال» وراجع الصفوف، ثم «أرسل».', 'تابع الحالات في «الدعوات المرسلة»: بانتظار القبول، قبلها، رفضها، ملغاة.'],
    tips: ['الرسالة إلى وليّ الأمر عامة لا يُذكر فيها اسم الطفل.', 'الدعوة المرسلة من قبل لا تُكرَّر، وتصلح ثلاثين يوماً، وتُلغى ما دامت بانتظار القبول.'],
  },
  '/app/home-training': {
    what: 'التدريب المنزلي بالفيديو: يرسل الأخصائي فيديو تعليمياً قصيراً لأسرة الطالب بعد الجلسة أو مراجعةً أسبوعية، والأسرة تضغط «تم التدريب» وترفع فيديو ردّ — وهنا نسبة التزام الأسر لكل طالب وأخصائي وللمؤسسة.',
    steps: ['من ملف الطالب، تبويب «التدريب المنزلي»: اختر جلسة منتهية أو «مراجعة أسبوعية»، اكتب العنوان وأرفق الفيديو.', 'يصل وليّ الأمر تنبيه؛ يشاهد ثم يضغط «تم التدريب» ويرفع فيديو ردّ إن شاء.', 'اقرأ هنا نسبة الالتزام: ما أُقرّ به ÷ ما أُرسل في آخر ثلاثين يوماً.', 'شعار التميّز يُمنح للمؤسسة تلقائياً حين تبلغ نسبةُ الالتزام وتغطيةُ الجلسات العتبتين — ويُسحب حين تنزل.'],
    tips: ['لا يُرسل فيديو بلا موافقة مشاركة سارية من وليّ الأمر.', 'الفيديو لا يغادر المنصة: تنزيلٌ مصادَق بلا رابط عام، ويُكنس بعد مدّة الاحتفاظ.', 'عتباتُ الشعار ومدّةُ الاحتفاظ في قاعدة R-014 من «القواعد» — لا في الكود.'],
  },
  '/app/availability': {
    what: 'دوامُ الكادر داخل المؤسسة: أيامُ العمل وساعاتُه وطولُ الفتحة بتوقيت عمّان — منه تُقترح الفتحاتُ الفارغة عند جدولة الجلسات فلا يُخمَّن الوقت.',
    steps: ['أضف نافذةً لكل يوم عمل: من الساعة إلى الساعة وطول الجلسة.', 'احفظ — الحفظُ يستبدل الجدولَ كلَّه.', 'الإدارة والمشرف: اختر عضو الكادر أولاً ثم اضبط دوامه.', 'عند جدولة جلسة تظهر الفتحاتُ الفارغة للأخصائي وللطالب فتختار منها.'],
    tips: ['من لا دوامَ له تبقى جدولتُه مفتوحةً بلا اقتراح.', 'الفتحةُ اقتراحٌ — والمنصة ترفض التعارض عند الجدولة مهما كان المصدر.'],
  },
  '/app/transport': {
    what: 'الباص: مساراتُ النقل بمرافقيها وركّابها على الخريطة — بيوتُ الأطفال يضعها أولياءُ الأمور فتصير محطّاتٍ، والمسارُ يُرتَّب بأفضل وقت، وراعي الباص يشغّل التتبّع من جواله فيصل الأهلَ تنبيهٌ عند كل محطّةٍ ويرون الباصَ حيّاً، ويسجّل الركوبَ والنزول ويؤكّد «نزل الجميع» قبل الإغلاق.',
    steps: ['الإدارة: أنشئ المسار وسمِّ مرافقاً، وحدّد موقعَ المؤسسة على الخريطة من بطاقة «الخريطة والمسار».', 'ضع المحطّات اليدوية على الخريطة، واطلب من أولياء الأمور تحديد بيوتهم من «طفلي في الباص» — ثم «رتّب المسار».', 'من «الأوقات والمدى» حدّد انطلاق الصباح والظهر، ومن «من يُنبَّه بالاقتراب» الحساباتِ التي يصلها تنبيهُ قرب الباص من المؤسسة.', 'راعي الباص (سائقٌ ومرافق — حسابُه موظفٌ إداري أو أيُّ عضوٍ سمّته الإدارةُ مرافقاً): افتح «رحلة اليوم» ← «شغّل التتبّع من هذا الجوال» واترك الصفحة مفتوحة؛ اضغط «ركب» و«نزل» لكل طالب، و«وصلتُ» إن ضعف GPS.', 'بعد نزول الجميع: «نزل الجميع» واكتب العبارة حرفياً — فيُبلَّغ الأهلُ بالوصول — ثم «أغلق الرحلة».', 'الطريق: تحت الخريطة «المحطّة التالية» بمسافتها ووقتها وتعليماتِ الانعطاف بالعربية، والخطُّ على الخريطة هو الطريقُ من موقع الباص عبر المحطّات الباقية حتى المدرسة — ويتحدّث مع التتبّع، أو اضغط «من موقعي الآن».'],
    tips: ['السجلُّ لا يُعدَّل ولا يُحذف: ما قيل للأسرة يبقى كما قيل.', 'لا تُغلق رحلةٌ وفيها من لم يُسجَّل نزولُه، ولا قبل تأكيد «نزل الجميع» إن ركب أحد.', 'الخريطةُ ومحرّكُ المسارات على خادم رفرفة داخل الأردن — لا تُرسل بيوتُ الأطفال ولا موقعُ الباص إلى أي جهةٍ خارجية.', 'اشتراكُ مقاعد النقل في الفوترة هو سقفُ عدد الركّاب؛ وبلا اشتراكٍ لا سقف.'],
  },
  '/app/attendance': {
    what: 'الحضور اليومي: حاضر، غائب، متأخر — لكل طالبٍ في نطاقك.',
    steps: ['اختر اليوم (اليوم افتراضاً).', 'علّم حالة كل طالب واحفظ.', 'الغياب والتأخّر يصلان وليّ الأمر تنبيهاً.', 'لتصحيح ما حُفظ: اختر اليوم نفسه وغيّر الحالة واحفظ — يبقى تحت اسم الطالب من سجّله ومن عدّله ومتى.'],
    tips: ['لا يُسجَّل حضورٌ لغدٍ ولا لأقدم من ستين يوماً — وإدارةُ المؤسسة تصحّح أيَّ يومٍ مضى.'],
  },
  '/app/reports': {
    what: 'التقارير: تُكتب بعد الجلسة، وتُراجَع، ثم تُنشر لكل جمهورٍ بنصّه.',
    steps: ['افتح تقرير الجلسة المنتهية.', 'اكتب نصّ الكادر ونصّ الأسرة كلٌّ في حقله.', 'أرسله للمراجعة، ثم يُنشر بعد الاعتماد.'],
  },
  '/app/caseload': {
    what: 'الإسناد: من الأخصائي أو المرشد المسؤول عن كل طالب — والرؤية تُفتح بالإسناد وحده.',
    steps: ['اختر الطالب ثم المختصّ والمسار.', 'احفظ الإسناد — يرى المختصّ الطالب فوراً.', 'أنهِ الإسناد حين ينتقل الطالب — لا يُحذف بل يُنهى بتاريخ.'],
  },
  '/app/counseling': {
    what: 'خطط الإرشاد وجلساته للطلبة المسندين إليك بمسار الإرشاد.',
    steps: ['افتح طالباً مُسنداً إليك.', 'أنشئ خطة الإرشاد وفعّلها — خطةٌ نشطة واحدة لكل طالب.', 'سجّل الجلسات وملاحظاتك الخاصة التي لا يراها غيرك.'],
  },
  '/app/referrals': {
    what: 'الإحالات إلى التربية الخاصة أو الإرشاد: توثيقٌ قبل القرار، ثم قبولٌ أو رفضٌ بسبب.',
    steps: ['أنشئ الإحالة بسببها وما يوثّقها.', 'قرّرها: الإرشاد يُقبل إلزامياً، والتربية الخاصة تُقبل أو تُرفض بسبب.', 'القبول يُسند الطالب للمختصّ المختار.'],
  },
  '/app/users': {
    what: 'حسابات مؤسستك وعضوياتها: الأدوار والمسميات والمشرف المباشر والحالات وآخر دخول.',
    steps: ['أنشئ حساب مشرفٍ داخلي أو معلّم أو موظفٍ إداري من «أنشئ حساباً».', 'الأخصائي والمرشد يُدعيان من «الدعوات» بحساباتهم المهنية.', 'من «المسمى» اكتب مسمّاه كما تسمّيه مؤسستك، واختر مشرفَه المباشر (مشرف المرحلة أو الإدارة).', 'من «الصلاحيات» فوّض شخصاً واحداً عن الإدارة (يربط الحسابات ويطلب المقاعد)، وامنح موظفاً إدارياً «الحضور والغياب» إن احتاج.', 'غيّر الحالة أو أنهِ الارتباط من أزرار الصفّ.'],
    tips: ['المسمّى عندكم لا يغيّر مسمّاه في حسابه المهني، والمشرفُ المباشر للتنظيم ولا يمنح صلاحية.', 'الموظف الإداري يراسل الكادر وأولياء الأمور ولا يفتح ملفات الأطفال؛ والأخصائي لا يعدّل عملَه أحدٌ فوقه — الإدارة تقرأ وتعتمد فقط.', 'المشرف المباشر على أخصائيٍّ أو مرشدٍ أخصائيٌّ بحسابٍ مهني من النوع نفسه — يُدعى من صفحة الدعوات، لا الإدارة ولا مشرفٌ أُنشئ مباشرة.'],
  },
  '/app/invitations': {
    what: 'دعوة أخصائيين ومرشدين — ومشرفين مهنيين عليهم — بحساباتهم المهنية القائمة إلى مؤسستك.',
    steps: ['ابحث عن المهني ببريده أو رقم حسابه.', 'أرسل الدعوة بالدور والمسار.', 'ينضمّ حين يقبلها هو — لا تُلصق عضويةٌ بلا قبول.'],
    tips: ['المشرف على الأخصائيين أخصائيٌّ مثلهم: ادعُه بدور «مشرف داخلي» ومسارُه يطابق نوعَ حسابه المهني.'],
  },
  '/app/credentials': {
    what: 'شهادات الكادر ورخصه وتواريخ انتهائها.',
    steps: ['ارفع الشهادة بملفّها وتاريخ صدورها وانتهائها.', 'تابع ما يقترب انتهاؤه.'],
  },
  '/app/settings': {
    what: 'إعدادات المؤسسة: المدينة والتواصل والمهل والخيارات العامة.',
    steps: ['عدّل الحقل واحفظ.', 'ما يظهر للعامة يُضبط من «الهوية والظهور العلني».'],
  },
  '/app/branding': {
    what: 'هوية المؤسسة وظهورها العلني: الشعار والنبذة والألبوم وما يظهر في دليل المراكز.',
    steps: ['ارفع الشعار واكتب النبذة.', 'قرّر الظهور العلني — افتراضه «لا».', 'الأعداد والألبوم خياران منفصلان فوق الظهور.'],
  },
  '/app/alerts': {
    what: 'التنبيهات التي تولّدها قواعد المؤسسة: خطرٌ يحتاج معالجة.',
    steps: ['افتح التنبيه واقرأ سببه.', 'أسنده لمن يعالجه.', 'أغلقه بنصّ معالجة — الحرج تغلقه إدارة المؤسسة وحدها.'],
  },
  '/app/indicators': {
    what: 'مؤشرات الجودة والحماية للمؤسسة بمعاييرها وأوزانها.',
    steps: ['اقرأ الدرجة ومعاييرها.', 'المخالفة الحرجة تظهر مع الدرجة ولا تدخل حسابها.'],
  },
  '/app/excellence': {
    what: 'درجات الكادر المركّبة بترتيب الاسم — لإدارة المؤسسة وحدها.',
    steps: ['افتح درجة الموظف لترى تفصيلها وحجم عيّنتها.', 'لا ترتيب ولا لوحة شرف — الدرجة تقيس شخصاً لا تقارنه.'],
  },
  '/app/broadcast': {
    what: 'تسجيلُ الحصص — تُسجَّل ولا تُبثّ مباشرة، ويُحفظ التسجيل مضغوطاً أربعة أيام ثم يُحذف. لا يبدأ بلا موافقة سارية لكل طالبٍ في الصفّ، ولا بلا صلاحية «تسجيل الحصة» للمعلّم، ولكل مؤسسةٍ سقفٌ شهري.',
    steps: ['اختر الصفّ.', 'تحقّق من الموافقات — من تنقصه موافقة يمنع البدء.', 'ابدأ التسجيل، ثم «سجّل من جهازي» لتصل الكاميرا والصوت، وأنهِه من الصفحة نفسها.', 'راقب «تسجيلات هذا الشهر» — بلوغُ السقف يوقف البدء حتى الشهر القادم.'],
    tips: ['سقفُ الشهر تضعه إدارةُ المنصة لضبط تكلفة الخوادم، وتستطيع المؤسسة أن تختار سقفاً أقلّ من «اضبط السياسة».'],
  },
  '/app/regulatory-scope': {
    what: 'الجهات الرسمية المرتبطة بمؤسستك وما تراه منها.',
    steps: ['راجع الروابط النشطة.', 'كل قراءةٍ من الجهة الرقابية مسجّلة وتظهر لك.'],
  },
  '/app/integration': {
    what: 'ملفّ التكامل مع الجهات الخارجية وما يُرسل إليها حقلاً حقلاً.',
    steps: ['راجع الحقول الخارجة في خريطة الحقول.', 'فعّل الملفّ أو أوقفه — كل تغييرٍ مدقَّق.'],
  },
  '/app/billing': {
    what: 'الاشتراك والفواتير ومقاعد الطلبة.',
    steps: ['راجع الباقة والفواتير.', 'اطلب زيادة المقاعد — إدارة المنصة تعتمد وتصدر الفاتورة.'],
  },
  '/app/jobs': {
    what: 'إعلانات الوظائف ومن تقدّم إليها.',
    steps: ['أنشئ الإعلان بالمسمى والمدينة.', 'راجع المتقدّمين — بيانات اتصالهم تظهر بعد قبولهم الدعوة.'],
  },
  '/app/continuity': {
    what: 'حزمة الاستمرارية: ما اعتُمد ونُشر عن الطالب ينتقل معه إلى مؤسسةٍ أخرى بموافقة وليّ أمره.',
    steps: ['اصدر الحزمة — تحتاج موافقة مشاركةٍ سارية.', 'الرمز يصل وليّ الأمر لا المؤسسة الأخرى.'],
  },
  '/app/audit': {
    what: 'سجلّ التدقيق: من فعل ماذا ومتى وعلى ماذا — وما رُفض ولماذا.',
    steps: ['ابحث باسم أو رقمٍ مرجعي أو فعل.', 'صفِّ بالمدّة والنتيجة.', 'صدّر النتيجة CSV — والتصدير نفسه يُسجَّل.'],
  },
  '/app/overview': {
    what: 'نظرة عامة على أعداد المؤسسة وحالاتها.',
    steps: ['اقرأ الأعداد بالفترة.', 'اضغط الرقم لتنتقل إلى قائمته.'],
  },
  '/app/news': {
    what: 'أخبار المنصة ومؤسستك.',
    steps: ['اقرأ الخبر.', 'إدارة المؤسسة تنشر أخبار مؤسستها من الشاشة نفسها.'],
  },
  '/app/forms': {
    what: 'النماذج الرقمية: نماذج المؤسسة الورقية (الملف الإداري، دراسة الحالة، الخطة التربوية الفردية، سجلّ المتابعة، تقرير الأداء الشهري، قوائم الرصد، خطة تعديل السلوك، فحص المعزّزات…) تُملأ داخل المنصة وتُطبع — بدل رفع ملفات.',
    steps: ['هنا القوالب: قوالب المنصة جاهزة، وخذ «نسخة للتعديل» لتعدّلها لمؤسستك أو استورد قالباً من ملف.', 'نموذج الطالب يُملأ من ملفه، تبويب «النماذج»: اختر القالب فيُملأ رأسه من بياناته.', 'احفظ مسودة ناقصة متى شئت، وحين تكتمل اضغط «أنهِ النموذج» فيصير وثيقة تُقرأ وتُطبع.', 'النماذج التي لا تخصّ طالباً (سجلّ الطلبة للعام) من تبويب «نماذج المؤسسة المملوءة».'],
    tips: ['النموذج النهائي لا يُعدَّل — الإدارة تعيد فتحه بسبب يُسجَّل.', 'تعديل القالب يرفع إصداره ولا يمسّ ما مُلئ قبله.'],
  },
  '/app/goal-bank': {
    what: 'بنك الأهداف وأساليب تعديل السلوك — يُفتح بعد الإقرار بتعهّد الاستعمال.',
    steps: ['أقرّ بالتعهّد عند أول فتح.', 'صفِّ بالقسم والمجال والعمر.', 'انسخ الهدف إلى خطتك وعدّله بما يناسب الطالب.'],
  },
  '/app/required-fields': {
    what: 'الحقول الإلزامية في ملفات الطلبة لمؤسستك.',
    steps: ['اختر نوع الملف ثم الحقل.', 'اجعله إلزامياً — يُفحص عند كل حفظ.'],
  },
  '/app/attachments': {
    what: 'رفع مرفقٍ على ملف طالب أو خطته أو تحضيره من مكانٍ واحد.',
    steps: ['اختر الطالب.', 'اختر على ماذا يُرفق: الملف، أو خطة، أو تحضير.', 'ارفع الملف — يظهر في مكانه من ملف الطالب.'],
  },
  '/me/messages': {
    what: 'رسائل الطلبة الذين في نطاقك ولهم حسابات — خيطٌ لكل طالب بينك وبينه وحده.',
    steps: ['غير المقروء أولاً.', 'افتح الخيط لتقرأ وتردّ — يُعلَّم مقروءاً.'],
  },
  '/app/my-attendance': {
    what: 'حضوري ودوامي: تسجّل قدومك عند وصولك وانصرافك عند مغادرتك بموقع جوالك، وتطلب مغادرةً أثناء الدوام، وترى ساعات شهرك.',
    steps: ['اضغط «سجّل حضوري» عند وصولك واسمح بالموقع — يظهر «داخل الموقع» أو «خارج الموقع».', 'اضغط «سجّل انصرافي» عند مغادرتك.', 'لمغادرةٍ أثناء الدوام اكتب اليوم والوقتين والسبب ثم «أرسل الطلب» — تعتمده الإدارة.', 'في «شهري» ترى الساعات المطلوبة والمداوَمة والمغادرات.'],
    tips: ['خارج الموقع لا يمنع التسجيل — يظهر للإدارة فقط.', 'نسيتَ التسجيل؟ تثبّته الإدارة على موعد دوامك.'],
  },
  '/app/staff-attendance': {
    what: 'حضور الكادر: من حضر اليوم ومتى ومن أين، والدوامُ الثابت لكل موظف، والمغادرات، والتقرير الشهري.',
    steps: ['في «اليوم» ترى كل موظفٍ بقدومه وانصرافه وموقعه، وتثبّت الحضور أو الانصراف على موعد الدوام أو تعدّله بوقتٍ تكتبه.', 'في «الدوام» اضبط أيام كل موظفٍ وساعاته — وطبّقها على الكادر كله بضغطة.', 'في «المغادرات» اعتمد الطلبات أو ارفضها بسبب، ومن «اليوم» سجّل مغادرةً بنفسك.', 'في «التقرير الشهري» الساعاتُ المطلوبة والمداوَمة والغياب والتأخّر والمغادرات — واطبعه.'],
    tips: ['حدّد موقع المؤسسة على الخريطة من «البطاقة التعريفية» ليُقاس «داخل الموقع».', 'نصفُ قطر الموقع يُضبط من «اليوم».'],
  },
  '/app/home-session': {
    what: 'تسجيل جلسة وإرسالها للأهل: الطالب من جدول اليوم، والهدف، والتحضير، والتسجيل بالكاميرا، وعدّاد المحاولات، والتوصيات — ثم «أنهِ وأرسل».',
    steps: ['اختر الجلسة من جدول اليوم أو الطالب من القائمة.', 'اختر الهدف من الخطة والتحضير المسجّل.', 'افتح الكاميرا واختر الأمامية أو الخلفية، ثم «ابدأ التسجيل» و«أنهِ التسجيل».', 'سجّل أداء الطالب بعدّاد المحاولات واكتب التوصيات وتعليمات الأهل، ثم «أنهِ وأرسل للأهل».'],
    tips: ['يحتاج الإرسال موافقة «مشاركة البيانات» من وليّ الأمر — تظهر له تلقائياً بانتظار توقيعه.', 'التسجيل حتى دقيقتين ونصف تقريباً.'],
  },
  '/app/leave': {
    what: 'طلبات الإجازة: تقدّم طلبك بنوعه وتاريخيه وسببه ومرفقه، ويمرّ بسلسلة الموافقة التي اختارها المركز.',
    steps: ['في «طلباتي» اختر النوع والتاريخين واكتب السبب، وأرفق تقريراً طبياً إن وُجد، ثم «قدّم الطلب».', 'في «بانتظار قراري» تجد ما ينتظر موافقتك — افتحه ووافق أو ارفض بسببٍ مكتوب.', 'يصلك القرار تنبيهاً، وتسحب طلبك ما دام معلَّقاً.'],
    tips: ['الإجازة السنوية تُطلب قبل يومها، والمرضية والطارئة تُقبل بعده.', 'لا يقرّر أحدٌ طلب إجازته.'],
  },
  '/app/complaints': {
    what: 'صندوق الشكاوى والاقتراحات: يصل إدارة المؤسسة وحدها، والكادر يختار إظهار اسمه أو إخفاءه.',
    steps: ['اكتب العنوان والشكوى، واختر «أخفِ اسمي» أو «أظهر اسمي»، ثم «أرسل للإدارة».', 'تجد شكاواك وردود الإدارة عليها هنا، وتردّ في الخيط نفسه.', 'للإدارة: افتح الشكوى وردّ عليها، ثم أغلقها بملاحظة ما عولج.'],
    tips: ['المخفيُّ اسمُه لا يظهر للإدارة ولا في سجلّ التدقيق، ولا تكشفه إلا إدارة المنصة بسببٍ مكتوب.'],
  },
  '/app/approvals': {
    what: 'من يوافق على ماذا في مؤسستك: طلبات الإجازة، ومراجعة الخطط واعتمادها، ومراجعة التقارير.',
    steps: ['لكل خطوة اختر: المشرف المباشر، أو شخصاً محدَّداً (مثل المنسّق)، أو الإدارة.', 'احفظ السلسلة — تسري على ما يُقدَّم من الآن، لا على الطلبات القائمة.', '«أعد الافتراض» يرجع السلسلة إلى ما كانت عليه.'],
    tips: ['لا يظهر لخطوةٍ إلا من يحمل صلاحيتها، ومن كتب الخطة أو راجعها لا يعتمدها مهما اخترت.'],
  },
  '/app/holidays': {
    what: 'عطل مؤسستك فوق العطل الرسمية في المملكة — كلُّها تظهر في التقويم ويتخطّاها توليدُ جلسات الخطة.',
    steps: ['العطل الرسمية تصل وحدها ولا تُحذف من هنا.', 'لعطلةٍ خاصة بمؤسستك: اختر اليوم واكتب اسمها ثم «أضف العطلة».', 'احذف عطلة مؤسستك من صفّها إن أُلغيت.'],
    tips: ['حذفُ عطلةٍ لا يغيّر الجلسات المجدولة سابقاً — يغيّر ما يُولَّد بعده.'],
  },
  '/platform/holidays': {
    what: 'عطل المملكة الرسمية التي تصل كلَّ المؤسسات وتظهر في تقاويمها.',
    steps: ['العطل الهجرية تُضاف بموعدٍ تقديري — صحّحها حين يُعلَن الموعد: احذف التقديري وأضف المُعلَن.', 'علّم «موعد تقديري» لما لم يُعلَن بعد.'],
    tips: ['البذرةُ لا تعيد سنةً فيها عطلةٌ واحدة — فما صحّحتَه لا يرجع مع إعادة التشغيل.'],
  },
  '/app/org-chart': {
    what: 'كادر مؤسستك شجرةً: كلُّ موظفٍ تحت مشرفه المباشر، والإدارةُ في أعلاها.',
    steps: ['من لا مشرفَ له يظهر في أعلى الشجرة.', 'لتنقل موظفاً تحت مشرف: «المستخدمون» ← تعديل الملف ← المشرف المباشر.', 'اطبع الهيكل من زرّ الطباعة.'],
    tips: ['الهيكلُ للتنظيم والعرض — لا يغيّر ما يراه أحدٌ أو ما يستطيع فعله.'],
  },
  '/app/messages': {
    what: 'رسائل الكادر داخل مؤسستك، ومجموعاتٌ لمن لهم صلةٌ بالعمل نفسه، ورسائل أولياء الأمور عن أبنائهم — كلُّ ما يُكتب يبقى.',
    steps: ['في «الكادر» اختر زميلاً — غيرُ المقروء أولاً — واكتب رسالتك؛ تصله إشعاراً.', 'في «المجموعات» أنشئ مجموعةً باسمٍ واختر أعضاءها من زملائك في المؤسسة؛ ثم اكتب نصاً أو أرفق ملفاً (صورة، PDF، وورد/إكسل/باوربوينت، فيديو) يراه الأعضاء وحدهم.', 'من أنشأ المجموعة يضيف ويُخرج ويسمّي ويؤرشف، وإدارةُ المؤسسة تدير كلَّ مجموعة، والعضو يغادر بنفسه.', 'في «أولياء الأمور» اختر الطالب ثم وليَّ أمره المفعَّل واكتب رسالتك عنه.', 'لرسالةٍ واحدة لأولياء أمور عدّة طلبة: حدّد الخانات بجانب أسمائهم (أو «حدّد الكل») ثم «راسل أولياء أمور المحدَّدين» — تصل كلَّ وليّ أمرٍ في خيط طفله.', 'فتحُ الخيط يعلّم الوارد مقروءاً فيرى المرسِل «قُرئت».'],
    tips: ['الرسائل لا تُحذف، والمجموعة تُؤرشف ولا تُحذف. والموظف الإداري عملُه هنا: يراسل الكادر وأولياء الأمور ولا يفتح ملفات الأطفال.'],
  },

  // ─── المعلّم ───
  '/me/class': {
    what: 'صفّك: طلبتك وملاحظاتهم الشهرية وواجباتهم ورسائلهم ووثائقهم — ومن هنا تُحيل من يحتاج.',
    steps: ['اضغط الطالب لتفتح صفحته: واجباته ورسائله ووثائقه الثبوتية.', 'اكتب ملاحظة الشهر السلوكية.', 'اضغط «أحِل» لتحيل الطالب إلى الإرشاد أو التربية الخاصة بسببٍ مكتوب.'],
    tips: ['إحالةُ الإرشاد تُقبل فوراً؛ وإحالةُ التربية الخاصة يقرّرها الأخصائي على ما تكتبه من أدلة.'],
  },
  '/me/referrals': {
    what: 'إحالاتك التي أرسلتَها لطلبتك ونتيجةُ كلٍّ منها — والرفضُ يأتي بسببٍ مكتوب.',
    steps: ['أحِل الطالب من «طلبتي» بزرّ «أحِل».', 'تابع هنا: قُبلت، أو تنتظر، أو رُفضت ولماذا.'],
  },
  '/me/timetable': {
    what: 'جدول حصصك الأسبوعي في كل صفوفك — تضعه إدارة المدرسة من صفحة كل صف.',
    steps: ['اقرأ حصص كل يوم: المادة والوقت والصف والقاعة.', 'اطبعه من زرّ الطباعة.'],
    tips: ['أيُّ تعديلٍ تجريه الإدارة يظهر هنا فوراً — لا تحتاج نسخةً ورقية.'],
  },
  '/app/classrooms': {
    what: 'الصفوف والشُّعب: لكل صفٍّ اسمٌ وشعبةٌ ومربٍّ ومعلّمو موادّ وطلبةٌ وجدولُ حصص.',
    steps: ['أنشئ الصف باسمه وشعبته واختر مربّيه.', 'من صفحة الصف أضف معلّمي المواد وطلبةَ الصف.', 'ابنِ جدول الحصص يوماً يوماً واحفظه — المنصة ترفض التعارض.', 'أرسل رسالةً لأولياء أمور الصف كلِّهم من زرٍّ واحد.'],
    tips: ['ضمُّ الطالب إلى الصف يضعه في روستر معلّمي الصف تلقائياً — فلا يُسند مرتين.', 'المعلّم يرى صفوفَه وحدها ولا يعدّلها.'],
  },
  '/app/academics': {
    what: 'الإعداد الأكاديمي: الفصول الدراسية، والمواد لكل مرحلة، ومكوّنات التقييم (مشاركة، واجبات، اختبارات قصيرة، نصفي، نهائي) — عليها يُرصد وتُقرأ الكشوف وبطاقات الطلبة.',
    steps: ['اضغط «طبّق القالب القياسي» فتُوضع فصلا السنة ومواد كل مرحلةٍ لها صفٌّ عندكم وخمسة مكوّنات مجموعها 100.', 'عدّل ما يخالف خطتكم: أضف مادة، غيّر حصصها، أوقف مكوّناً.', 'اجعل فصلاً واحداً «الحالي» — عليه يرصد المعلّمون.', 'بعد اكتمال الرصد: «انشر النتائج» فتصل البطاقة الطالبَ ووليَّ أمره ويُقفل الرصد على المعلّمين.'],
    tips: ['الرصد نفسه من صفحة الصف، تبويب «الدرجات».', 'اسم المادة هنا هو ما يحمله معلّم المادة في الصف — به تعرف المنصة من يرصد.', 'ما يُوقف لا يُمحى: درجاته المرصودة تبقى في الكشوف.'],
  },
  '/app/worksheets': {
    what: 'أوراق العمل: يرفعها المعلّم (PDF أو صورة) ويرسلها لصفٍّ كامل أو لطلبةٍ يختارهم — والمشرفُ يرى عدّاد الشهر.',
    steps: ['اكتب العنوان واختر الملف.', 'اختر صفاً كاملاً أو طلبةً بأسمائهم.', 'أرسل — يصل كلَّ طالبٍ واجباً باسمه ويُبلَّغ وليُّ أمره.', 'المشرف والإدارة: اختر الشهر واقرأ كم أرسل كلُّ معلّم.'],
    tips: ['مراجعةُ ما سلّمه الطالب من «الواجبات».'],
  },
  '/me/assignments': {
    what: 'الواجبات التي أرسلتها لطلبتك وحالتها: مُرسَل، سُلّم، رُوجع.',
    steps: ['أنشئ واجباً: مهمة أو تمرينٌ من الفهرس.', 'راجع ما سُلّم واكتب ملاحظتك.'],
  },
  '/me/roster-plans': {
    what: 'الخطط المعتمدة لطلبتك وما شاركه المختصّون معك، بأهدافها واستراتيجياتها.',
    steps: ['افتح خطة الطالب.', 'اعمل على أهدافها في الصفّ بالاستراتيجية المذكورة.'],
  },

  // ─── وليّ الأمر ───
  '/me/children': {
    what: 'أبناؤك: إضافتهم، وربطهم بمؤسسة (أو قبول دعوة مدرسة وصلتك ببريدك)، وملفّهم التراكمي، وتقاريرهم، وتمارينهم، وفيديوهات التدريب المنزلي، وجلساتهم الإلكترونية.',
    steps: ['أضف طفلك — يُعطى رقماً مرجعياً RF-….', 'اربطه بمؤسسة من دليل المراكز — تحتاج بريداً موثَّقاً.', 'افتح «الملف التراكمي» لترى ما يُعمل معه.', 'من «الجلسات الإلكترونية» ادخل جلسة طفلك عن بُعد حين يبدأها الأخصائي — من جوالك بالكاميرا والميكروفون.'],
  },
  '/me/reports': {
    what: 'التقارير المنشورة لك عن أبنائك.',
    steps: ['اختر التقرير لتقرأه.', 'التقارير الشهرية والفصلية من بطاقة الطفل.'],
  },
  '/me/recommendations': {
    what: 'توصيات المرشد المنشورة لك.',
    steps: ['اقرأ التوصية وما يُطلب منك في البيت.'],
  },
  '/me/consents': {
    what: 'موافقاتك: التسجيل، والبثّ، والصور، والمشاركة — لكل مؤسسةٍ على حدة.',
    steps: ['اقرأ نصّ الموافقة كاملاً.', 'وقّعها أو اسحبها — السحب يسري فوراً.'],
  },
  '/me/continuity': {
    what: 'حزم الاستمرارية لأبنائك: رمزٌ تحمله إلى المؤسسة الجديدة.',
    steps: ['انسخ الرمز من الحزمة.', 'سلّمه للمؤسسة الجديدة لتستوردها مرةً واحدة.'],
  },

  // ─── الحساب المهني والاستشارات والأكاديمية ───
  '/me/professional': {
    what: 'ملفّك المهني: لك وحدك، ينتقل معك بشهاداتك وسجلّك وأوسمتك.',
    steps: ['أكمل المؤهل والنبذة والمحافظة والخبرات.', 'قرّر الظهور في صفحة الكوادر — افتراضه «لا».', 'ارفع سيرتك — تقرؤها إدارات المراكز لا العامة.'],
  },
  '/me/invitations': {
    what: 'دعوات المؤسسات لك.',
    steps: ['اقرأ الدعوة بدورها ومسارها.', 'اقبلها أو اعتذر — لا تُلصق بك عضويةٌ بلا قبولك.'],
  },
  '/me/jobs': {
    what: 'الوظائف المعلنة على المنصة.',
    steps: ['صفِّ بالمدينة والمسمى.', 'تقدّم — لا يخرج بريدك ولا جوالك قبل قبولك دعوة المؤسسة.'],
  },
  '/me/excellence': {
    what: 'درجتك المركّبة وتفصيلها وحجم عيّنتها.',
    steps: ['اقرأ كل معيارٍ وما يرفعه.', 'الشهادة تُصدر حين تبلغ العتبة والعيّنة.'],
  },
  '/me/news': {
    what: 'أخبار مؤسستك والمنصة.',
    steps: ['اقرأ الخبر من القائمة.'],
  },
  '/me/services': {
    what: 'خدماتك الاستشارية: العنوان وسعر الساعة وأنماط الاتصال.',
    steps: ['أضف الخدمة وسعرها.', 'فعّلها لتظهر في التصفّح وتُحجز.'],
  },
  '/me/availability': {
    what: 'تقويمك الأسبوعي: الأيام والأوقات التي تستقبل فيها الحجوزات، بتوقيت عمّان.',
    steps: ['أضف نوافذك الأسبوعية.', 'ارفع يوماً بعينه إن غبت.', 'الحفظ يستبدل الجدول كلّه.'],
  },
  '/me/consultations': {
    what: 'استشاراتك: الطلبات والمواعيد والجلسات وتسويتها.',
    steps: ['اقبل الطلب أو اعتذر.', 'ابدأ الاستشارة في موعدها وأنهِها — يُحسب المال فوراً.'],
  },
  '/me/wallet': {
    what: 'محفظتك: الرصيد والمحجوز ودفتر القيود وطلبات السحب.',
    steps: ['اقرأ القيود — كل حركةٍ لها قيد.', 'اطلب السحب — يُخصم لحظة الطلب ويعود إن رُفض.'],
  },
  '/me/course-provider': {
    what: 'طلب أن تصير مقدّم دورات: برقم حسابك المهني ورخصتك وهويتك.',
    steps: ['اكتب رقم حسابك المهني كما في ملفّك.', 'ارفع الرخصة والهوية PDF.', 'بالاعتماد تُفتح «دوراتي» و«أكاديميتي».'],
  },
  '/me/academy': {
    what: 'أكاديميتك: بياناتها وبابها ومنشوراتها ومشتركوها، وملاحظات إدارة المنصة عنها.',
    steps: ['أنشئها وانتظر اعتمادها — تنال رقماً AC-….', 'حدّد الباب: مجاني أو مدفوع بمدّة.', 'انشر منشوراتٍ للعامة أو للأعضاء أو مدفوعة.'],
  },
  '/me/academy/manage': {
    what: 'الأكاديمية من الداخل: الفصول (حتى ثلاثة) والرسائل والواجبات إلى الملتحقين، والتقويم الأكاديمي، وقائمة الملتحقين بفصولهم.',
    steps: ['أنشئ فصولك (حتى ثلاثة) من قسم «الأكاديمية».', 'من قسم «الملتحقون» اختر من تريد وضعه في فصل.', 'أرسل رسالةً أو واجباً إلى الكلّ أو إلى فصل — يصلهم تنبيهاً فوراً.', 'أضف مواعيدك في «التقويم الأكاديمي» فتظهر عند الملتحقين.'],
  },
  '/me/courses': {
    what: 'دوراتك: إنشاؤها ونشرها وتجديد إعلانها.',
    steps: ['أنشئ الدورة بمدّتها وشهادتها.', 'انشرها بأيام إعلانٍ تُخصم من محفظتك.', 'جدّد الإعلان قبل انقضائه.'],
  },
  '/academy': {
    what: 'الأكاديمية: الدورات والأكاديميات الافتراضية على المنصة.',
    steps: ['اختر الدورات أو الأكاديميات.', 'افتح الدورة وسجّل — المدفوعة تُخصم من محفظتك.'],
  },
  '/me/enrollments': {
    what: 'دوراتك المسجَّلة واشتراكاتك في الأكاديميات وشهاداتك.',
    steps: ['افتح الدورة لتتابعها.', 'الشهادة تُصدر عند الإتمام برمزٍ يُتحقَّق منه علناً.'],
  },
  '/services': {
    what: 'الخدمات الاستشارية المتاحة للحجز.',
    steps: ['اختر الاستشاري والخدمة.', 'اختر موعداً من الأوقات المتاحة واحجز — يُحجز المبلغ من رصيدك.'],
  },

  // ─── الجهة الرقابية ───
  '/oversight': {
    what: 'المؤسسات المرتبطة بجهتك وما تحتها: الكادر والطلبة والخطط والجلسات والتقارير والتنبيهات.',
    steps: ['اختر المؤسسة.', 'افتح ما تريد مراجعته — القراءة وحدها.'],
    tips: ['كل قراءةٍ تُسجَّل وتراها المؤسسة: من يراقب يُرى.'],
  },

  // ─── إدارة المنصة وأقسامها ───
  '/platform': {
    what: 'لوحة اليوم لإدارة المنصة وأقسامها: أرقام ما يخصّ صفتك وحدها وروابطها.',
    steps: ['ابدأ بما ينتظرك.', 'اختصاراتك في أعلى الصفحة — خصّصها بما تحتاجه كل يوم.'],
  },
  '/platform/institutions': {
    what: 'كل المؤسسات على المنصة وحالاتها، والكشف على كلٍّ منها.',
    steps: ['ابحث عن المؤسسة.', 'افتح «اكشف عليها» لترى أعدادها وكادرها — بلا ملفات أطفال.'],
  },
  '/platform/finance': {
    what: 'المالية: الإيرادُ بمصادره، وضريبةُ المبيعات بفتراتها، وقائمةُ الدخل، والذممُ والاشتراكات، وأموالُ المحافظ، والمصروفات.',
    steps: ['اختر المدى (السنةُ المالية حتى اليوم افتراضاً).', 'اضبط «إعدادات الضريبة والعملة» مرّةً: التسجيلُ والرقمُ والنسبة وهل الأسعارُ شاملة.', 'قيّد المصروفاتَ بفواتيرها لتُخصم ضريبةُ المدخلات ويظهر الربح.', 'صدّر دفترَ الحركات CSV للمحاسب.'],
  },
  '/platform/growth': {
    what: 'نموّ المنصة: مجاميع بلا أسماء، بلا الحسابات التجريبية.',
    steps: ['اختر الفترة.', 'اقرأ الأعداد بالشهر.'],
  },
  '/platform/institution-requests': {
    what: 'طلبات تسجيل المراكز والمدارس — لا تدخل الجهة المنصة قبل القبول.',
    steps: ['راجع بيانات الطلب ورخصته.', 'اقبله — تُنشأ المؤسسة ومسؤولها بكلمةٍ مؤقتة.', 'أو ارفضه بسبب.'],
  },
  '/platform/seat-requests': {
    what: 'طلبات زيادة مقاعد الطلبة من المؤسسات.',
    steps: ['راجع العدد المطلوب.', 'اعتمده — تزيد المقاعد وتصدر الفاتورة في المعاملة نفسها.'],
  },
  '/platform/regulatory': {
    what: 'الجهات الرقابية وروابطها بالمؤسسات.',
    steps: ['أنشئ الجهة.', 'اربطها بمؤسسة واضبط الإشراف الكامل.'],
  },
  '/platform/regulators': {
    what: 'حسابات المشرفين الرقابيين — حساباتٌ مستقلة لا صفةٌ تُلصق بموظف.',
    steps: ['أنشئ الحساب لجهته ببريدٍ لا يحمل صفةً أخرى.', 'سلّم الكلمة المؤقتة لصاحبها بقناةٍ آمنة.'],
  },
  '/platform/accounts': {
    what: 'كل حسابات المنصة في صفحةٍ واحدة — بمرشّحٍ متقدّم في الرابط وتصدير.',
    steps: ['ابحث بالاسم أو البريد أو الجوال أو الرقم المرجعي.', 'افتح «تصفية متقدّمة» واختر النوع والحالة والمدّة.', 'افتح الحساب لترى ملفّه الكامل — وفتحه يُسجَّل.'],
    tips: ['انسخ رابط الصفحة لتشارك التصفية نفسها مع زميل.'],
  },
  '/platform/admins': {
    what: 'حسابات إدارة المنصة وأقسامها: الإنشاء وتغيير القسم والإسقاط.',
    steps: ['أنشئ الحساب واختر قسمه.', 'سلّم الكلمة المؤقتة مرةً واحدة.', 'غيّر القسم أو أسقط الصفة من أزرار الصفّ.'],
    tips: ['لا يغيّر أحدٌ قسمه بنفسه، ولا تنزل آخر إدارةٍ كاملة إلى قسم.'],
  },
  '/platform/deletion-requests': {
    what: 'طلبات حذف الحسابات من أصحابها — الحذف إخفاء هويةٍ لا محو.',
    steps: ['راجع الطلب وتعهّد صاحبه.', 'اعتمده أو ارفضه بملاحظة.'],
  },
  '/platform/credentials': {
    what: 'شهادات الكوادر: صورُ الشهادات التي رفعها المهنيون — توثّقها فتظهر في دليل الكوادر أو تحجبها بسبب.',
    steps: ['افتح «بانتظار التوثيق» وراجع الصورة أو الملف.', 'اضغط «وثّق» أو اكتب السبب واضغط «احجب».'],
    tips: ['يصل صاحبَ الشهادة إشعارٌ بالقرار.'],
  },
  '/platform/contact-messages': {
    what: 'رسائل «تواصل معنا» العلنية.',
    steps: ['اقرأ الرسالة ونوعها.', 'تواصل مع صاحبها ثم علّمها «عولجت» بملاحظة — لا تُمحى.'],
  },
  '/platform/sandbox': {
    what: 'الدخول إلى الحسابات التجريبية بصلاحياتها هي — لتجربة الشاشات بعين أصحابها.',
    steps: ['اختر الحساب.', 'ادخل — والخروج يعيدك إلى حسابك.'],
  },
  '/platform/pricing': {
    what: 'تسعير المنصة: رسوم الاستشارات والدورات والأكاديميات وحدّ السحب.',
    steps: ['اقرأ «ما معنى كل رسم؟» قبل التعديل.', 'عدّل واحفظ — ما بيع قبل التغيير لا يُعاد تسعيره.'],
  },
  '/platform/payouts': {
    what: 'طلبات السحب من محافظ المهنيين.',
    steps: ['راجع الطلب ورصيد صاحبه.', 'اعتمده أو ارفضه — الرفض يعيد المبلغ في المعاملة نفسها.'],
  },
  '/platform/complaints': {
    what: 'شكاوى المستفيدين على استشاراتٍ تمّت، ومعها تسجيلها.',
    steps: ['اقرأ الشكوى واستمع للتسجيل.', 'احسمها مرةً واحدة بقرارٍ مكتوب.'],
  },
  '/platform/news': {
    what: 'أخبار المنصة العامة.',
    steps: ['اكتب الخبر وصورته.', 'انشره — يظهر لكل المؤسسات والعامة.'],
  },
  '/platform/partners': {
    what: 'شركاء النجاح: الجهات والبرامج الداعمة التي تظهر شعاراتها في الصفحة الأولى بالترتيب الذي تختاره.',
    steps: ['أضف الشريك باسمه بالعربية والإنجليزية ورابط موقعه إن وُجد.', 'ارفع شعاره — صورة مربّعة واضحة على خلفية فاتحة.', 'رتّب الشعارات بالسهمين، وأخفِ ما لا تريد عرضه مؤقتاً بدل حذفه.'],
  },
  '/platform/forms': {
    what: 'قوالب النماذج الرقمية التي تراها كل المؤسسات وتنسخها لتعدّل نسختها.',
    steps: ['أنشئ قالباً أو عدّل مخطّطه (JSON) مع معاينة حيّة.'],
    tips: ['تعديل المخطّط يرفع الإصدار — وما مُلئ على إصدار سابق يبقى كما هو.', 'القالب يُؤرشف ولا يُحذف.'],
  },
  '/platform/goal-bank': {
    what: 'بنك أهداف المنصة وأساليب تعديل السلوك.',
    steps: ['أضف الهدف أو عدّله بحقول بطاقته.', 'ما تعدّله لا يعيده إقلاعٌ تالٍ.'],
  },
  '/platform/consent-texts': {
    what: 'نصوص الموافقات — لا تُعدَّل بعد نشرها، التعديل نسخةٌ جديدة.',
    steps: ['أنشئ نسخةً جديدة من النصّ.', 'انشرها — تسري على الموافقات الجديدة.'],
  },
  '/platform/course-providers': {
    what: 'طلبات مقدّمي الدورات بوثائقهم.',
    steps: ['نزّل الرخصة والهوية وراجعهما.', 'اعتمد الطلب أو ارفضه بسبب.'],
  },
  '/platform/consultants': {
    what: 'طلبات المهنيين لتفعيل الاستشارات الشخصية: مجالاتهم ونبذتهم وخبرتهم.',
    steps: ['اقرأ الطلب وافتح ملفه في الكوادر وشهاداته.', 'فعّل الاستشارات أو ارفض بسبب يقرؤه.'],
  },
  '/platform/academies': {
    what: 'الأكاديميات الجديدة التي تنتظر الاعتماد.',
    steps: ['راجع بيانات الأكاديمية ومالكها.', 'اعتمدها — تنال رقمها AC-… للتحقّق العلني.'],
  },
  '/platform/content-reports': {
    what: 'بلاغات المحتوى على منشورات الأكاديميات وتعليقاتها.',
    steps: ['اقرأ المحتوى المُبلَّغ عنه.', 'أخفِه أو أبقِه — الإخفاء يُبلغ صاحبه.'],
  },
  '/app/rules': {
    what: 'قواعد التنبيه: صفوفٌ تُعدَّل بلا نشر.',
    steps: ['افتح القاعدة واقرأ شرطها.', 'عدّل عتبتها أو أوقفها.'],
  },
  '/platform/notes': {
    what: 'ملاحظات التحسين التي يكتبها قسمك لجهةٍ من نوعه — تصل صاحبها ويردّ عليها.',
    steps: ['أنشئ الملاحظة: نوع الجهة، ثم الجهة، ثم العنوان والنصّ.', 'تابع الردود في خيطها.', 'علّمها «عولجت» ثم أغلقها — لا تُحذف.'],
  },
  '/platform/audit': {
    what: 'سجلّ التدقيق — كاملاً لإدارة المنصة، وأفعالك وحدها للأقسام.',
    steps: ['ابحث بالاسم أو الرقم المرجعي أو الفعل.', 'صفِّ بالمدّة والنتيجة.'],
  },
  '/platform/health': {
    what: 'صحة التشغيل: خادم المنصة وخادم المكالمات بحملهما (المعالج والذاكرة والقرص والشبكة والقاعدة والطلبات)، والتنبيهات بسببها ونصيحتها وحلّها المؤقّت والجذري، ومتى يُطوَّر الخادم، والنسخ الاحتياطي، والأخطاء غير المتوقعة.',
    steps: ['ابدأ بشريط «حرج/تحذير» أعلى الصفحة: صفرٌ = لا شيء يلزم.', 'في كل بند افتح «السبب» و«الحلّ المؤقّت» ونفّذه، و«الحلّ الجذري» للتخطيط.', 'راجع بطاقة «متى يُطوَّر الخادم» أسبوعياً — تحتاج يوماً من القياس أولاً.', 'تحقّق أن آخر نسخة احتياطية حديثة، وراجع الأخطاء المتكرّرة.'],
  },
  '/platform/call-test': {
    what: 'الاتصال الرقمي: غرفةُ اختبارٍ على خادم المكالمات تدخلها من جهازين لتتأكد أن الصوت والصورة والمشاركة تعمل قبل أن يشتكي أحد.',
    steps: ['اضغط «افتح غرفة اختبار» — تُنشأ غرفةٌ مؤقتة وتدخلها بالكاميرا والصوت.', 'انسخ «رابط الجهاز الثاني» وافتحه من جوالٍ أو متصفّحٍ آخر.', 'تأكد أن كلا الطرفين يرى الآخر ويسمعه، وجرّب المشاركة وملء الشاشة على الجوال.', 'الغرفة تُغلق وحدها بعد عشر دقائق فارغة — لا أثرَ لها في سجلّ المكالمات.'],
  },
};

/** «ابدأ من هنا» — ثلاث خطواتٍ أولى لكل صفة. المفتاحُ الصفةُ الأولى في `START_ORDER` التي يحملها الحساب */
export const START_HERE: Record<string, { title: string; steps: string[] }> = {
  PLATFORM_ADMIN: { title: 'إدارة المنصة', steps: ['راجع لوحة اليوم في «الرئيسية».', 'أنشئ حسابات الأقسام من «حسابات الإدارة».', 'تابع صحة التشغيل وآخر نسخة احتياطية.'] },
  PLATFORM_SUPPORT: { title: 'قسم خدمة المستفيدين', steps: ['افتح «رسائل التواصل» الجديدة.', 'ابحث عن صاحب المشكلة في «الحسابات» بالرقم المرجعي أو الجوال.', 'راجع الشكاوى المفتوحة.'] },
  PLATFORM_ACADEMIES: { title: 'قسم الأكاديميات والدورات', steps: ['راجع الأكاديميات وطلبات مقدّمي الدورات.', 'احسم بلاغات المحتوى.', 'اكتب ملاحظات التحسين لأصحابها.'] },
  PLATFORM_INSTITUTIONS: { title: 'قسم المراكز والمدارس', steps: ['راجع طلبات تسجيل المراكز والمدارس.', 'احسم طلبات المقاعد.', 'اكشف على المؤسسات واكتب ملاحظات التحسين.'] },
  PLATFORM_FINANCE: { title: 'القسم المالي', steps: ['راجع طلبات السحب المعلّقة.', 'تحقّق من تسعير المنصة.', 'راجع باقات الاشتراك.'] },
  REGULATORY_SUPERVISOR: { title: 'الجهة الرقابية', steps: ['افتح المؤسسات المرتبطة بجهتك.', 'راجع ما تحتاجه — القراءة وحدها.', 'تذكّر أن كل قراءةٍ مسجّلة وتراها المؤسسة.'] },
  INSTITUTION_ADMIN: { title: 'إدارة المؤسسة', steps: ['أكمل «ابدأ تشغيل مؤسستك» في اللوحة.', 'اقبل طلبات ربط الطلبة.', 'أنشئ حسابات الكادر وادعُ المختصّين.'] },
  INTERNAL_SUPERVISOR: { title: 'المشرف الداخلي', steps: ['افتح «مركز المراجعة».', 'راجع ما تجاوز مهلته أولاً.', 'اعتمد أو أعد بملاحظةٍ واضحة.'] },
  SPECIALIST: { title: 'الأخصائي', steps: ['افتح «الطلبة المسندون إليك».', 'اكتب الخطة وأرسلها للمراجعة.', 'حضّر الجلسة ثم ابدأها في موعدها.'] },
  COUNSELOR: { title: 'المرشد', steps: ['افتح «الطلبة المسندون إليك».', 'فعّل خطة الإرشاد.', 'سجّل الجلسات وملاحظاتك الخاصة.'] },
  TEACHER: { title: 'المعلّم', steps: ['افتح «صفّي».', 'سجّل الحضور كل صباح.', 'أرسل الواجبات وردّ على رسائل الطلبة.'] },
  PARENT: { title: 'وليّ الأمر', steps: ['أضف طفلك من «أبنائي».', 'وثّق بريدك من «حسابي».', 'اطلب ربط طفلك بمؤسسة من دليل المراكز.'] },
  PROFESSIONAL: { title: 'الحساب المهني', steps: ['أكمل ملفّك المهني.', 'اقبل دعوات المؤسسات.', 'أضف خدماتك وتقويمك إن كنت استشارياً.'] },
  CLIENT: { title: 'المستفيد', steps: ['تصفّح الخدمات الاستشارية.', 'احجز موعداً من الأوقات المتاحة.', 'تابع استشاراتك ومحفظتك.'] },
};
export const START_ORDER = ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_ACADEMIES', 'PLATFORM_INSTITUTIONS', 'PLATFORM_FINANCE', 'REGULATORY_SUPERVISOR', 'INSTITUTION_ADMIN', 'INTERNAL_SUPERVISOR', 'SPECIALIST', 'COUNSELOR', 'TEACHER', 'PARENT', 'PROFESSIONAL', 'CLIENT'];

/** أسئلةٌ شائعة — كلُّ جوابٍ يصف ما تفعله المنصة الآن */
export const FAQ: Array<{ q: string; a: string }> = [
  { q: 'لم يصلني رمز التحقق — ماذا أفعل؟', a: 'الرمز صالحٌ عشر دقائق. تأكّد من البريد المكتوب وافتح مجلّد الرسائل غير المرغوبة، ثم اطلب رمزاً جديداً بعد دقيقة. المستفيد يصله الرمز على واتساب.' },
  { q: 'نسيتُ كلمة المرور', a: 'من صفحة الدخول اختر «نسيت كلمة المرور»، واكتب بريدك فيصلك رمزٌ تعيد به تعيينها. إعادة التعيين تُنهي جلساتك المفتوحة.' },
  { q: 'ما التحقّق بخطوتين؟', a: 'رمزٌ من تطبيق مصادقةٍ على جوالك يُطلب مع كلمة المرور. فعّله من «حسابي»، واحفظ رموز الاسترداد في مكانٍ آمن — كلٌّ منها يُستعمل مرةً واحدة.' },
  { q: 'كيف أثبّت رفرفة على جوالي؟', a: 'على أندرويد: من قائمة المتصفّح اختر «تثبيت التطبيق». على آيفون: من سفاري اضغط زرّ المشاركة ثم «إضافة إلى الشاشة الرئيسية».' },
  { q: 'لماذا لا تصلني إشعارات الجوال؟', a: 'فعّلها من «حسابي» ← تفضيلات التنبيه، واسمح بها حين يسألك المتصفّح. على آيفون تعمل بعد تثبيت رفرفة على الشاشة الرئيسية. وجرّب «أرسل إشعاراً تجريبياً».' },
  { q: 'من يرى بيانات طفلي؟', a: 'المؤسسة التي ربطته بها، وفيها من أُسند إليه وحده. والتسجيلات والصور والبثّ تحتاج موافقتك، وسحبُ الموافقة يسري فوراً.' },
];

/** نسخةُ منصة الطفل: جملٌ قصيرة ورسمٌ لكل باب */
export const KID_HELP: Array<{ art: SectionKind; title: string; text: string; to: string }> = [
  { art: 'home', title: 'صفحتي', text: 'منها تذهب إلى كل مكان.', to: '/me/student' },
  { art: 'games', title: 'التمارين', text: 'العب وتعلّم. بعض الأوقات مقفلة للراحة.', to: '/me/student/games' },
  { art: 'homework', title: 'واجباتي', text: 'افتح الواجب وسلّمه حين تنتهي.', to: '/me/student/homework' },
  { art: 'grades', title: 'درجاتي', text: 'درجاتك في كل مادة بعد أن تنشرها مدرستك.', to: '/me/student/grades' },
  { art: 'inbox', title: 'رسائلي', text: 'اقرأ رسائل معلّميك واكتب لهم.', to: '/me/student/inbox' },
  { art: 'school', title: 'مدرستي', text: 'أخبار مدرستك وشعارها.', to: '/me/student/school' },
];
